import { MessageCircle, Star } from "lucide-react";

// Each WA screenshot has a tagline, a city tag, and a slight rotation/positioning
// to break the rigidity of a 4-column grid.
const SCREENS = [
  {
    src: "/images/whatsapp/wa-1.jpg",
    alt: "Message WhatsApp d'un voyageur Airbnb : ménage irréprochable, explications très claires pour l'arrivée, à bientôt à Saint-Malo",
    tag: "Saint-Malo",
    quote: "On n'a jamais vu un Airbnb aussi propre !",
    rotate: -3,
    delay: 0,
  },
  {
    src: "/images/whatsapp/wa-2.jpg",
    alt: "Message WhatsApp d'un voyageur Airbnb : on tenait à vous féliciter, ménage au top, linge propre, instructions hyper détaillées, on recommande à 100 %",
    tag: "Cancale",
    quote: "On recommande à 100 % !",
    rotate: 2,
    delay: 100,
  },
  {
    src: "/images/whatsapp/wa-3.jpg",
    alt: "Message WhatsApp d'un voyageur Airbnb : excellent séjour, logement très propre bien équipé conforme aux photos, indications parfaites, ne changez rien",
    tag: "Dinard",
    quote: "Merci beaucoup, ne changez rien !",
    rotate: -2,
    delay: 200,
  },
  {
    src: "/images/whatsapp/wa-4.jpg",
    alt: "Message WhatsApp d'un voyageur Airbnb : très bon séjour dans votre logement, tout très propre, instructions claires et complètes, merci pour votre accueil et votre réactivité",
    tag: "Damgan",
    quote: "Merci pour votre accueil et votre réactivité !",
    rotate: 3,
    delay: 300,
  },
];

const WhatsAppGallery = () => (
  <section
    className="py-20 lg:py-28 bg-gradient-to-br from-cream via-sand to-cream relative overflow-hidden"
    data-testid="whatsapp-gallery"
  >
    {/* Subtle background dots */}
    <div
      aria-hidden="true"
      className="absolute inset-0 opacity-[0.03] pointer-events-none"
      style={{
        backgroundImage:
          "radial-gradient(circle, #0F2C4F 1px, transparent 1px)",
        backgroundSize: "28px 28px",
      }}
    />

    <div className="container-tm relative">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto mb-14 lg:mb-16">
        <div className="inline-flex items-center gap-2 mb-5">
          <span className="w-9 h-9 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-soft">
            <MessageCircle size={16} fill="currentColor" strokeWidth={0} />
          </span>
          <p className="overline text-ink/65 mb-0">
            Retours voyageurs · WhatsApp
          </p>
        </div>
        <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl text-ink leading-[1.05] tracking-tight">
          Les voyageurs nous écrivent
          <br />
          <em className="text-gold not-italic">spontanément.</em>
        </h2>
        <p className="mt-6 font-sans text-base md:text-lg text-ink/65 leading-relaxed">
          Captures d'écran réelles de WhatsApp, ce qu'écrivent les voyageurs
          en quittant nos logements. Quand le ménage est impeccable et
          l'accueil chaleureux, ils prennent le temps de remercier.
        </p>
        <div className="mt-6 flex justify-center items-center gap-1 text-gold">
          {[...Array(5)].map((_, i) => (
            <Star key={i} size={18} fill="currentColor" strokeWidth={0} />
          ))}
          <span className="ml-3 text-ink/55 text-sm font-medium">
            Note Airbnb moyenne : 4,92 / 5
          </span>
        </div>
      </div>

      {/* Scattered gallery */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 max-w-7xl mx-auto">
        {SCREENS.map((s, i) => (
          <figure
            key={i}
            data-testid={`whatsapp-screen-${i}`}
            className={`group relative ${
              i % 2 === 0 ? "lg:mt-0" : "lg:mt-10"
            }`}
            style={{
              animation: `wa-fade-in 600ms ${s.delay}ms both ease-out`,
            }}
          >
            {/* Tag tilted */}
            <span
              className="absolute -top-3 left-4 z-10 inline-flex items-center gap-1.5 bg-gold text-ink text-[10px] uppercase tracking-[0.18em] font-semibold rounded-full px-3 py-1 shadow-soft"
              style={{ transform: `rotate(${s.rotate * -1}deg)` }}
            >
              <Star size={10} fill="currentColor" strokeWidth={0} /> {s.tag}
            </span>

            {/* Phone mockup */}
            <div
              className="relative bg-white rounded-[28px] overflow-hidden shadow-soft border border-ink/5 transition-transform duration-500 ease-out group-hover:rotate-0 group-hover:scale-[1.03]"
              style={{ transform: `rotate(${s.rotate}deg)` }}
            >
              <img
                src={s.src}
                alt={s.alt}
                loading="lazy"
                decoding="async"
                className="block w-full h-auto"
              />
            </div>

            {/* Caption */}
            <figcaption
              className="mt-4 text-center font-serif italic text-ink/75 text-sm leading-snug px-2"
              style={{ transform: `rotate(${s.rotate * 0.3}deg)` }}
            >
              « {s.quote} »
            </figcaption>
          </figure>
        ))}
      </div>

      {/* Footer note */}
      <p className="mt-14 text-center text-ink/50 text-xs italic max-w-2xl mx-auto">
        Numéros de téléphone et identités floutés conformément au RGPD.
        Captures d'écran originales archivées et disponibles sur demande.
      </p>
    </div>

    {/* Local keyframes (only used by this component) */}
    <style>{`
      @keyframes wa-fade-in {
        from { opacity: 0; transform: translateY(20px) rotate(0deg); }
        to { opacity: 1; }
      }
    `}</style>
  </section>
);

export default WhatsAppGallery;
